import { isSiteRole, type Account, type PlanFit, t } from '@procumi/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { clearAsyncCache } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { rich } from '../lib/rich';

/**
 * Pachetul a fost micșorat sub ce folosește firma (6 colegi, pachet de 2). Până
 * alege administratorul cine rămâne activ și ce șantiere rămân deschise, serverul
 * nu primește nicio modificare; administratorul vede alegerea în locul paginii,
 * ceilalți văd paginile, cu o bandă care le spune de ce nu pot schimba nimic.
 */
export function PlanFitGate({
  account,
  toast,
  onChoosePlan,
  children,
}: {
  account: Account;
  toast: Toast;
  onChoosePlan: () => void;
  children: ReactNode;
}) {
  const [fit, setFit] = useState<PlanFit | null>(null);
  const { plan } = account.company;
  // contul de șantier n-are acces la datele firmei (serverul răspunde 403); nici nu ocupă loc în pachet
  const site = isSiteRole(account.user.role);
  useEffect(() => {
    if (!plan || site) return setFit(null);
    let alive = true;
    api
      .planFit()
      .then((f) => alive && setFit(f))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [plan, site]);

  if (!fit) return <>{children}</>;
  if (!account.user.isAdmin) {
    return (
      <>
        <div className="plan-banner over" role="status">
          <span>
            {rich(
              '<b>Pachetul firmei s-a schimbat în {0}.</b> Până alege administratorul cine rămâne activ, poți doar să vezi.',
              fit.plan,
            )}
          </span>
        </div>
        {children}
      </>
    );
  }
  return (
    <FitChooser
      fit={fit}
      me={account.user.id}
      onChoosePlan={onChoosePlan}
      onDone={() => {
        clearAsyncCache();
        setFit(null);
        toast(t('Gata, firma încape în pachetul nou'));
      }}
    />
  );
}

function FitChooser({
  fit,
  me,
  onChoosePlan,
  onDone,
}: {
  fit: PlanFit;
  me: string;
  onChoosePlan: () => void;
  onDone: () => void;
}) {
  const [users, setUsers] = useState<Set<string>>(new Set([me]));
  const [sites, setSites] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const flip = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };
  const usersOk = !fit.users || users.size <= fit.users.limit;
  const sitesOk = !fit.sites || sites.size <= fit.sites.limit;

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const left = await api.fitPlan({ keepUsers: [...users], keepSites: [...sites] });
      if (left) setError(t('Firma tot nu încape în pachet. Reîncarcă pagina și încearcă din nou.'));
      else onDone();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="fit">
      <div className="head">
        <div>
          <h1>{t('Pachetul firmei s-a schimbat în {0}', fit.plan)}</h1>
          <p className="head-facts">
            {t(
              'Firma folosește acum mai mult decât are pachetul. Alege ce rămâne activ; până atunci nimeni din firmă nu poate schimba nimic.',
            )}
          </p>
        </div>
        <button className="btn" type="button" onClick={onChoosePlan}>
          {t('Vreau un pachet mai mare')}
        </button>
      </div>

      {fit.users ? (
        <div className="sheet fit-part">
          <div className="fit-h">
            <h2>{t('Cine rămâne activ')}</h2>
            <span className={usersOk ? 'fit-n' : 'fit-n over'}>
              {t('{0} din {1}', users.size, fit.users.limit)}
            </span>
          </div>
          <p className="hint">
            {t(
              'Ceilalți ies din cont pe loc. Îi poți readuce oricând din „Contul firmei → Echipa”, dacă treci la un pachet mai mare. Ce au lucrat rămâne, cu numele lor. Invitațiile încă neacceptate se retrag. Oamenii de pe șantier nu ocupă loc și rămân toți.',
            )}
          </p>
          <div className="checks">
            {fit.users.people.map((p) => (
              <label key={p.id}>
                <input
                  type="checkbox"
                  checked={users.has(p.id)}
                  disabled={p.id === me}
                  onChange={() => setUsers(flip(users, p.id))}
                />
                <span>
                  <b>{p.name}</b>
                  {p.id === me ? ' (tu)' : p.isAdmin ? t(' · administrator') : ''}
                  <small>{p.email}</small>
                </span>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {fit.sites ? (
        <div className="sheet fit-part">
          <div className="fit-h">
            <h2>{t('Ce șantiere rămân deschise')}</h2>
            <span className={sitesOk ? 'fit-n' : 'fit-n over'}>
              {t('{0} din {1}', sites.size, fit.sites.limit)}
            </span>
          </div>
          <p className="hint">
            {t(
              'Celelalte se închid: rămân în istoric, cu cererile și comenzile lor, dar nu mai primesc cereri noi. Le poți redeschide când termini unul dintre cele active.',
            )}
          </p>
          <div className="checks">
            {fit.sites.list.map((s) => (
              <label key={s.id}>
                <input
                  type="checkbox"
                  checked={sites.has(s.id)}
                  onChange={() => setSites(flip(sites, s.id))}
                />
                <span>
                  <b>{s.name}</b>
                  {s.locality ? <small>{s.locality}</small> : null}
                </span>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="anaf-warn" role="alert">
          {error}
        </p>
      ) : null}
      <div className="fit-f">
        <button
          className="btn primary"
          type="button"
          disabled={busy || !usersOk || !sitesOk}
          onClick={() => void save()}
        >
          {busy ? t('Se salvează…') : t('Confirmă alegerea')}
        </button>
        {!usersOk || !sitesOk ? (
          <span className="hint">
            {!usersOk
              ? t(
                  'Debifează {0} {1}. ',
                  users.size - fit.users!.limit,
                  users.size - fit.users!.limit === 1 ? t('coleg') : t('colegi'),
                )
              : ''}
            {!sitesOk
              ? t(
                  'Debifează {0} {1}.',
                  sites.size - fit.sites!.limit,
                  sites.size - fit.sites!.limit === 1 ? t('șantier') : t('șantiere'),
                )
              : ''}
          </span>
        ) : null}
      </div>
    </section>
  );
}
